/**
 * Cycle 5 auditing rework (WI-11) — the FE-side spelling of the auditing wire contracts.
 *
 * <p>Every type here mirrors a C# record the API already serves, field-for-field and
 * spelling-for-spelling. The API's JSON is snake_case; these read it RAW rather than through
 * `transformKeys`, because the auditing reads are point reads whose bodies are nested maps
 * (`results` is keyed by register-item id) and a blanket key transform would rewrite the ITEM IDS
 * as if they were field names.</p>
 *
 * <p>⛔ <b>`AuditPhase` is a WIRE value, not a label.</b> `AuditRunPhases` on the server says so in
 * as many words: *"these are the PERSISTED wire values and the lease-key suffixes; they are not
 * display labels and must not be localized."* The display strings live in the `auditing` locale
 * namespace and are looked up FROM these values — never the other way round.</p>
 */

/** The two auditing phases, as `AuditRunPhases` persists them. */
export type AuditPhase = 'design_effective' | 'operating_effective';

export const AUDIT_PHASES: readonly AuditPhase[] = ['design_effective', 'operating_effective'];

/**
 * TD-1037 — the SCOPE of a completeness/coverage verdict, as `?scope=` on the completeness
 * endpoints and as `CompletenessScope` on the server.
 *
 * <p>`undefined` is the workspace-wide check — one verdict per plan, the shipped row and the
 * shipped id, which validation and development keep unchanged. The two phase values select that
 * phase's OWN verdict: §F4.1.2 gives each phase its own coverage answer, and before this they
 * shared one row and silently overwrote each other.</p>
 *
 * <p>⛔ <b>Advisory.</b> `the-truth.md` §1 principle 3 — a coverage verdict never blocks a run, at
 * any scope. The only thing that disables Run is an in-flight check or ingest.</p>
 */
export type CompletenessScope = AuditPhase;

export function isAuditPhase(value: unknown): value is AuditPhase {
  return typeof value === 'string' && (AUDIT_PHASES as readonly string[]).includes(value);
}

/** `AuditRunStatuses`. `completed` means EVERY register item produced a verdict. */
export type AuditRunStatus = 'queued' | 'running' | 'completed' | 'failed';

/** The server's own terminal predicate, restated so no caller re-derives it from a list. */
export function isTerminalRunStatus(status: string | undefined): boolean {
  return status === 'completed' || status === 'failed';
}

/** A run is ACTIVE while it is neither completed nor failed. The in-progress block keys off this
 *  and nothing else — §F4.1.3: the block "appears ONLY while a run is active". */
export function isActiveRun(run: Pick<AuditRun, 'status'> | null | undefined): boolean {
  return run != null && !isTerminalRunStatus(run.status);
}

export interface AuditRunError {
  code: string;
  detail?: string | null;
  item_id?: string | null;
}

export interface AuditRunProgress {
  items_planned: number;
  items_completed: number;
  /** Server read projection from the run's own persisted artifacts. Missing on an older API. */
  generated_item_ids?: string[] | null;
  failed_item_ids?: string[];
  pending_item_ids?: string[] | null;
  items_failed?: number | null;
  items_pending?: number | null;
  phases_planned?: string[];
  /** The engine STAGE — `generating` | `extracting` | `aggregating` | `checking` | `sequencing` | `done`.
   *  Never a workflow state. Null until the engine has reported once. */
  current_phase?: string | null;
  percent: number;
}

export interface AuditRunItemResult {
  /** The verdict id from the CK vocabulary. ⛔ Never matched against a code-side literal — the FE
   *  renders `verdictLabel(verdict)`, which falls back to the raw id rather than to a guess. */
  verdict: string;
  section_id: string;
  version_id: string;
  finding_count: number;
  /** Rev 9 §7 — the model's short reason for the verdict, recorded as written. */
  explanation?: string;
  /** Rev 9 §7 — the run-wide source numbers the item cites (the run's own reference list). */
  citation_ids?: string[];
  /** Rev 9 §7 — true for the CK-marked inconclusive verdict: a data gap, not a conclusion. */
  data_gap?: boolean;
  /** Tools whose results the analysis could not use (persisted on the run-owned version). Null /
   *  absent ⇒ not degraded. Rendered only as a localized notice — never the tool names. */
  context_degraded_tools?: string[] | null;
}

export interface AuditRun {
  id: string;
  workspace_id: string;
  phase: AuditPhase;
  version: number;
  derived_from_run_id?: string | null;
  status: AuditRunStatus;
  file_ids: string[];
  context_run_id?: string | null;
  /** Rev 9 §3 / §6 — the APPROVED checklist this run analysed, pinned at trigger time. */
  checklist_plan_id?: string | null;
  checklist_plan_version?: number | null;
  started_by?: string | null;
  started_at: string;
  ended_at?: string | null;
  error?: AuditRunError | null;
  progress: AuditRunProgress;
  /** Keyed by register-item id. */
  results: Record<string, AuditRunItemResult>;
  /** Verdict tallies, counted server-side. ⛔ The FE never re-derives an overall rating from
   *  these — there is none, by decision. */
  summary: Record<string, number>;
  llm_io_ref?: string | null;
}

/** `POST /audit/runs` — 202 Accepted body. Nothing renders from it: the caller REFETCHES the
 *  roster. It is typed so the run id can be used to select which row to open, not to draw one. */
export interface AuditRunTriggerAccepted {
  run_id: string;
  phase: AuditPhase;
  version: number;
  status: AuditRunStatus;
  items_planned: number;
  file_ids: string[];
  context_run_id?: string | null;
}

export type ReportVersionStatus = string;

export interface ReportVersionSectionRef {
  section_id: string;
  version_id: string;
  version: number;
}

export interface ReportVersion {
  id: string;
  workspace_id: string;
  version: number;
  plan_id: string;
  plan_version: number;
  design_run_id?: string | null;
  operating_run_id?: string | null;
  file_ids: string[];
  section_versions: ReportVersionSectionRef[];
  content_sha256?: string | null;
  status: ReportVersionStatus;
  derived_from_report_version_id?: string | null;
  generated_by?: string | null;
  generated_at: string;
  ended_at?: string | null;
  error?: { code: string; detail?: string | null } | null;
  /** The fingerprint verdict persisted when the version was minted. `rejected_count` is how many
   *  source files were terminally rejected at ingestion and left out of this version's evidence. */
  ingest_gate?: {
    files_considered: number;
    skipped_unchanged: number;
    rejected_count?: number;
    rejected_file_ids?: string[];
  } | null;
}

/**
 * `GET /report-versions`.
 *
 * <p>🔑 <b>`current_report_version_id` is READ, never derived.</b> The endpoint names it explicitly
 * for exactly this reason: a failed V3 sorts above a current V2, so `max(version)` would open the
 * wrong bar. The FE has one question — which bar is open — and the server has already answered it.</p>
 */
export interface ReportVersionRoster {
  report_versions: ReportVersion[];
  current_report_version_id?: string | null;
}

// ────────────────────────────────────────────────────────────────────────────────────────────────
// Auditing rework rev 9 — the three sequential plans (report plan → DE checklist → OE checklist)
// ────────────────────────────────────────────────────────────────────────────────────────────────

/** `plan_versions.plan_kind`. A wire value, never a label. */
export type PlanKind = 'report' | 'de_checklist' | 'oe_checklist';

/** The short phase tag the checklist routes, the evidence map and the catalog use. */
export type AuditPhaseTag = 'de' | 'oe';

export function phaseTagOfPhase(phase: AuditPhase): AuditPhaseTag {
  return phase === 'design_effective' ? 'de' : 'oe';
}

export function phaseOfTag(tag: string | null | undefined): AuditPhase | null {
  if (tag === 'de' || tag === 'design_effective') return 'design_effective';
  if (tag === 'oe' || tag === 'operating_effective') return 'operating_effective';
  return null;
}

export function checklistKindOfPhase(phase: AuditPhase): PlanKind {
  return phase === 'design_effective' ? 'de_checklist' : 'oe_checklist';
}

/** One checklist item, as `ChecklistEndpoints` persists it. `id` is server-minted. */
export interface ChecklistItem {
  id: string;
  title: string;
  /** The criteria. */
  description: string;
  method?: string | null;
  /** OE items only: the approved DE items this item tests. */
  de_item_refs?: string[];
}

/** A checklist plan row (`GET /plan?kind=`, `/plan/{id}/versions/{v}`, and the checklist routes). */
export interface Checklist {
  plan_id: string;
  version: number;
  /** `draft` | `approved` (a `failed` row is never served as current). */
  status: string;
  items: ChecklistItem[];
}

/**
 * `GET /plan?kind=<checklist>` — the current checklist, or none; when none, whether a generation
 * holds the persisted lease right now (hydration recovery: a reload mid-generation reads it back).
 */
export interface ChecklistRead {
  checklist: Checklist | null;
  generationInProgress: boolean;
  /**
   * D7 — the typed error code the last background generation ended with, read from its persisted
   * failure record; null/absent when none. Only meaningful while `checklist` is null.
   */
  generationFailed?: string | null;
}

/** The editable fields of a checklist item. `id` is never sent. */
export interface ChecklistItemFields {
  title?: string;
  description?: string;
  method?: string;
  de_item_refs?: string[];
}

/** `PATCH /plan/{planId}/checklist/items` — the structural edit (§3a). */
export interface ChecklistEdit {
  base_plan_version: number;
  modified_items?: { id: string; fields: ChecklistItemFields }[];
  added_items?: { after_id?: string | null; item: ChecklistItemFields }[];
  removed_ids?: string[];
}

/** One checklist as the report preflight names it. */
export interface PreflightChecklist {
  plan_id: string | null;
  version: number | null;
  status: string | null;
  items: { id: string; title: string }[];
}

/** One frozen run as the report preflight names it. */
export interface PreflightRun {
  run_id: string;
  phase: AuditPhase;
  version: number;
  status: string;
  ended_at?: string | null;
  summary?: Record<string, number>;
}

/** `GET /evidence-map?scope=report` → `preflight`. Keyed by phase tag. */
export interface AuditAdmissionDecision {
  allowed: boolean;
  reason: string | null;
  error_args: Record<string, unknown>;
}

/** Missing or contradictory decisions are transport defects, never admission. */
export function isAuditAdmissionDecision(value: unknown): value is AuditAdmissionDecision {
  if (value == null || typeof value !== 'object') return false;
  const d = value as Partial<AuditAdmissionDecision>;
  return typeof d.allowed === 'boolean'
    && (d.allowed ? d.reason === null : typeof d.reason === 'string' && d.reason.length > 0)
    && d.error_args != null && typeof d.error_args === 'object' && !Array.isArray(d.error_args);
}

export interface ReportPreflight {
  unlock: { unlocked: boolean; missing_phases: AuditPhase[] };
  admission: AuditAdmissionDecision;
  checklists: Record<AuditPhaseTag, PreflightChecklist | null>;
  runs: Record<AuditPhaseTag, PreflightRun | null>;
}

function isPreflightChecklist(value: unknown): value is PreflightChecklist {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return false;
  const c = value as Partial<PreflightChecklist>;
  return typeof c.plan_id === 'string' && c.plan_id.trim().length > 0
    && typeof c.version === 'number' && Number.isInteger(c.version) && c.version > 0
    && typeof c.status === 'string' && c.status.length > 0
    && Array.isArray(c.items) && c.items.every((item) => item != null
      && typeof item.id === 'string' && item.id.trim().length > 0 && typeof item.title === 'string');
}

function isPreflightRun(value: unknown, phase: AuditPhase): value is PreflightRun {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return false;
  const r = value as Partial<PreflightRun>;
  return typeof r.run_id === 'string' && r.run_id.trim().length > 0 && r.phase === phase
    && typeof r.version === 'number' && Number.isInteger(r.version) && r.version > 0
    && typeof r.status === 'string' && r.status.length > 0;
}

export function isReportPreflight(value: unknown): value is ReportPreflight {
  if (value == null || typeof value !== 'object') return false;
  const p = value as Partial<ReportPreflight>;
  return p.checklists != null && p.runs != null && p.unlock != null
    && !Array.isArray(p.checklists) && !Array.isArray(p.runs)
    && (p.checklists.de === null || isPreflightChecklist(p.checklists.de))
    && (p.checklists.oe === null || isPreflightChecklist(p.checklists.oe))
    && (p.runs.de === null || isPreflightRun(p.runs.de, 'design_effective'))
    && (p.runs.oe === null || isPreflightRun(p.runs.oe, 'operating_effective'))
    && typeof p.unlock.unlocked === 'boolean' && Array.isArray(p.unlock.missing_phases)
    && p.unlock.missing_phases.every(isAuditPhase) && isAuditAdmissionDecision(p.admission)
    && (!p.admission.allowed || (p.unlock.unlocked && p.unlock.missing_phases.length === 0
      && p.checklists.de?.status === 'approved' && p.checklists.oe?.status === 'approved'
      && p.runs.de?.status === 'completed' && p.runs.oe?.status === 'completed'));
}
